import { useState } from "react";
import OrderCard from "./OrderCard";
import { Order, OrderStatus, PaymentStatus } from "../../types/orders";
import { useFetchOrders } from "../../hooks/orders/useFetchOrders";
import { useDebounce } from "src/utils/useDebounce";
import * as XLSX from "xlsx";
import { formatDateDDMM } from "src/utils";
import { toast } from "react-toastify";
import { TrashIcon } from "lucide-react";

function OrdersView() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<OrderStatus | "">("");
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | "">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);

  const page_size = 50;

  const debouncedSearch = useDebounce(search, 500);

  const {
    data: orders,
    isLoading,
    isFetching,
  } = useFetchOrders({
    search: debouncedSearch || undefined,
    status: status || undefined,
    payment_status: paymentStatus || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    page,
    page_size,
  });

  const clearFilters = () => {
    setSearch("");
    setStatus("");
    setPaymentStatus("");
    setDateFrom("");
    setDateTo("");
  };

  const exportOrdersToExcel = () => {
    try {
      const rows: Record<string, any>[] = [];

      Array.isArray(orders) &&
        orders?.forEach((order: Order) => {
          rows.push({
            "Order Number": order.order_number,
            "Transaction ID": order.transaction_id,
            "Order Status": order.status,
            "Payment Status": order.payment_status,
            "Ordered On": formatDateDDMM(order.created_at),
            "Product Name": order.order_items
              .map((item) => item.productName)
              .join(", "),
            "Order Total Amount": order.total_amount,
            "Referred By Name": order.referred_by_name,
            "Customer Name": order.address.name,
            "Phone Number": order.address.phone,
            City: order.address.city,
            State: order.address.state,
          });
        });

      const worksheet = XLSX.utils.json_to_sheet(rows);

      // column widths
      worksheet["!cols"] = [
        { wch: 20 },
        { wch: 15 },
        { wch: 15 },
        { wch: 25 },
        { wch: 18 },
        { wch: 25 },
        { wch: 20 },
        { wch: 35 },
        { wch: 25 },
        { wch: 20 },
        { wch: 30 },
        { wch: 12 },
      ];

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

      XLSX.writeFile(workbook, "supplement_orders.xlsx");
    } catch (error) {
      console.error("Error exporting orders:", error);
      toast.error("Failed to export Orders to Excel");
    }
  };

  return (
    <div className="space-y-6 animate-in slide-in-from-right-4">
      {/* Filters */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <input
            type="text"
            placeholder="Search orders by name..."
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            className="border border-slate-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />

          {/* Order Status Filter */}
          <select
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value as OrderStatus);
            }}
            className="border border-slate-200 rounded-xl px-4 py-2 text-sm font-medium focus:ring-2 focus:ring-emerald-500"
          >
            <option value="">Order Status</option>
            {[
              "CREATED",
              "PENDING",
              "PLACED",
              "SHIPPED",
              "DELIVERED",
              "CANCELLED",
            ].map((s) => (
              <option value={s}>{s}</option>
            ))}
          </select>

          {/* Payment Status Filter */}
          <select
            value={paymentStatus}
            onChange={(e) => {
              setPage(1);
              setPaymentStatus(e.target.value as PaymentStatus);
            }}
            className="border border-slate-200 rounded-xl px-4 py-2 text-sm font-medium focus:ring-2 focus:ring-emerald-500"
          >
            <option value="">Payment Status</option>
            <option value="PAID">Paid</option>
            <option value="FAILED">Failed</option>
          </select>

          <div className="flex items-center gap-2">
            <div className="relative">
              <label className="absolute -top-2 left-3 bg-white px-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                From
              </label>
              <input
                type="date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => {
                  setPage(1);
                  setDateFrom(e.target.value);
                  console.log(e.target.value);
                }}
                className="border border-slate-200 rounded-xl px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <span className="text-slate-400 text-sm font-bold">—</span>

            <div className="relative">
              <label className="absolute -top-2 left-3 bg-white px-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                To
              </label>
              <input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => {
                  setPage(1);
                  setDateTo(e.target.value);
                }}
                className="border border-slate-200 rounded-xl px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          <div className="flex items-center justify-center">
            <button onClick={clearFilters}>
              <TrashIcon className="size-6  hover:text-red-600 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Export Button */}
        <button
          className="bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 hover:bg-emerald-800 transition-all"
          onClick={exportOrdersToExcel}
        >
          Get Excel
        </button>
      </div>

      {/* Orders List */}
      <div className="grid grid-cols-1 gap-4">
        {isLoading && (
          <div className="text-center text-slate-400 py-10">
            Loading orders...
          </div>
        )}

        {!isLoading && orders?.length === 0 && (
          <div className="text-center text-slate-400 py-10">
            No orders found
          </div>
        )}

        {Array.isArray(orders) &&
          orders.map((order: Order) => (
            <OrderCard order={order} key={order.order_id} />
          ))}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-center gap-4 pt-4">
        <button
          disabled={page === 1}
          onClick={() => setPage((p) => p - 1)}
          className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold disabled:opacity-40 hover:bg-slate-50"
        >
          Previous
        </button>

        <div className="text-sm font-semibold text-slate-600">Page {page}</div>

        <button
          disabled={orders?.length < page_size}
          onClick={() => setPage((p) => p + 1)}
          className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold disabled:opacity-40 hover:bg-slate-50"
        >
          Next
        </button>
      </div>

      {/* Background Fetch Indicator */}
      {isFetching && !isLoading && (
        <div className="text-center text-xs text-slate-400">
          Updating orders...
        </div>
      )}
    </div>
  );
}

export default OrdersView;
